/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'
import { User, CurrentUserResponse } from './types'

export interface UserPolicies {
  activeLanguages?: string[]
  allowBlindedUsersToGenerateReports?: boolean
  allowKeepVersionsFromCR?: boolean
  allowPermanentDelete?: boolean
  allowPersonalizeClientDateformat?: boolean
  allowTouchId?: boolean
  allowUsersToManageChangeRequests?: boolean
  allowUserToDownloadWhenSourceCannotViewed?: boolean
  approverMustViewDocumentPriorToApproval?: boolean
  autoGenerateEtmfStudyStructures?: boolean
  clientDateFormat?: string
  clientTimeFormat24h?: boolean
  clientTimeFormatDelimiter?: string
  containsLegacyEOSStudy?: boolean | null
  defaultDateFormat?: string
  defaultTimeZoneId?: string
  disableRequiredProperties?: boolean
  enforcePasswordUpdates?: boolean
  fastWebViewPdf?: boolean
  forgeFontFallbackHandlingForWindows?: boolean
  hideStackedDocumentVersion?: boolean
  includeTimeZone?: boolean
  liveContentVerification?: boolean
  maxUnsuccessfulLoginAttempts?: number
  openTaskReminderFrequency?: number
  openTaskReminderNotificationFrequency?: number
  passwordUpdateFrequency?: number
  pollingInterval?: number
  raUserMustViewDocumentPriorToApproval?: boolean
  reportsPassword?: string
  reportTimeout?: number
  requireESignatureForFileCommands?: boolean
  requireUsernameInESignature?: boolean
  restrictSiteUserAccessToOwnedDocs?: boolean
  showEmailAddress?: boolean
  startOpenTaskReminderAfter?: number
  stopOpenTaskReminderAfter?: number
  timeFormat?: string
  timeFormat24h?: boolean
  useReportsPassword?: boolean
  webSessionTimeout?: number
  [key: string]: unknown
}

export interface BrowserFolder {
  id: number
  name: string
  systemFolderType: string
  [key: string]: unknown
}

export interface BrowserFoldersResponse {
  elements: BrowserFolder[]
}

export interface IntegrationsResponse {
  externalEditor?: string
  [key: string]: unknown
}

export interface LanguageInfo {
  localeTag: string
  nativeName: string
  englishName: string
  active: boolean
}

export interface UserSettings {
  timeFormat24h: boolean
  locale: string
  timeFormat: string
  dateFormat: string
  [key: string]: string | boolean
}

export interface RequestObjectType {
  target?: string
}

export interface LoginUrlResponseObjectType {
  token?: string
  loginUrl?: string
  imedidataBaseUrl?: string
}

class AuthApiService {
  async getCurrentLoggedInUser(): Promise<CurrentUserResponse> {
    return baseApiService.fetch<CurrentUserResponse>('/api/currentUser')
  }

  async login(email: string, password: string) {
    const authHeader = 'Basic ' + btoa(`${email}:${password}`)
    return baseApiService.fetch('/api/currentUser', {
      headers: {
        Authorization: authHeader
      }
    })
  }

  async logout() {
    return baseApiService.fetch('/api/logout', {
      method: 'DELETE'
    })
  }

  async checkPassword(password: string) {
    return baseApiService.fetch('/api/checkPassword', {
      method: 'POST',
      body: { password }
    })
  }

  async changeUserSettings(settings: UserSettings) {
    return baseApiService.fetch('/api/users/changeusersettings', {
      method: 'POST',
      body: settings
    })
  }

  async getUserPolicies(): Promise<UserPolicies> {
    const data = await baseApiService.fetch<UserPolicies>('/api/settings')

    // Parse pollingInterval to number if it's a string (matching legacy behavior)
    if (data.pollingInterval && typeof data.pollingInterval !== 'number') {
      data.pollingInterval = parseInt(String(data.pollingInterval), 10)
    }

    return data
  }

  async getBrowserFolders(): Promise<BrowserFoldersResponse> {
    return baseApiService.fetch('/api/v2/files', {
      method: 'GET',
      params: {
        systemFolderType: ['FORMS', 'TEMPLATES', 'REGULATED_CONTENT', 'WORKFLOWS', 'INIT_ETMF_FILES', 'REPORTS']
      }
    })
  }

  async getIntegrations(id: number): Promise<IntegrationsResponse> {
    return baseApiService.fetch(`/api/integrations/${id}`)
  }

  async getAllLanguages(): Promise<LanguageInfo[]> {
    return baseApiService.fetch('/api/settings/languages')
  }

  async getIMedidataLoginUrl(params: RequestObjectType): Promise<LoginUrlResponseObjectType> {
    return baseApiService.fetch('/api/imedidataloginurl', {
      method: 'GET',
      params: { ...params }
    })
  }

  async getIMedidataBaseUrl(): Promise<LoginUrlResponseObjectType> {
    return baseApiService.fetch('/api/checkmate/imedidatabaseurl')
  }

  async getCheckmateLoginUrl(params: RequestObjectType): Promise<LoginUrlResponseObjectType> {
    return baseApiService.fetch('/api/checkmate/loginurl', {
      method: 'GET',
      params: { ...params }
    })
  }

  async resetPassword(username: string) {
    return baseApiService.fetch('/api/resetpassword', {
      method: 'POST',
      body: {
        email: username
      }
    })
  }

  async setNewPassword(password: string, token: string) {
    // Legacy: new ApiBase().one(resetPswURL, token).customPUT({password})
    // URL: PUT /api/resetpassword/{token} with body {password}
    return baseApiService.fetch(`/api/resetpassword/${encodeURIComponent(token)}`, {
      method: 'PUT',
      body: {
        password
      }
    })
  }

  async validateResetPasswordToken(token: string) {
    // Legacy: new ApiBase({showLoader: false, setErrorInterceptor: false}).one(resetPswURL, token).get()
    // URL: GET /api/resetpassword/{token}
    return baseApiService.fetch(`/api/resetpassword/${encodeURIComponent(token)}`, {
      method: 'GET'
    })
  }

  /**
   * Validates authentication token by calling /api endpoint
   * Legacy: ApiAuth.validateToken(useDirectRCMInstance)
   *
   * @param useDirectRCMInstance - When true, uses api-url attribute as baseUrl and disables c_selections
   */
  async validateToken(useDirectRCMInstance?: boolean): Promise<any> {
    if (useDirectRCMInstance) {
      // For Direct RCM: use api-url attribute as baseUrl and disable c_selections
      // Matches legacy: {baseUrl: angular.element('html').attr('api-url'), disabledCSelections: true}
      const apiUrl = document.documentElement.getAttribute('api-url')
      if (!apiUrl) {
        throw new Error('api-url attribute not found for Direct RCM validation')
      }

      return baseApiService.fetch('/api', {
        baseUrl: apiUrl,
        disableCSelections: true
      })
    }

    // Standard token validation
    return baseApiService.fetch('/api')
  }
}

export const authApiService = new AuthApiService()
